/**
 * Shared helper for opening the sitewide JotForm AI coach.
 * One agent everywhere: Elara · Nervous System Wellness Coach, mounted
 * once globally via /public/index.html. The legacy keys (maya / elara /
 * peakState) all resolve to her so existing callers keep working.
 */

// Single coach — source of truth for the agent ID + full-screen URL.
const NSW_COACH = {
  label: "Elara · Nervous System Wellness Coach",
  agentId: "019fa7e36f007000819bdb22a50cac7939cf",
  fullScreenUrl:
    "https://integratedwellness.jotform.com/agent/019fa7e36f007000819bdb22a50cac7939cf",
};

const AGENTS = {
  maya: NSW_COACH,
  elara: NSW_COACH,
  peakState: NSW_COACH,
};

/**
 * Best-effort "open this floating chat" trigger. When given an `agentId`
 * we narrow the DOM cascade to that specific agent so Maya and Elara
 * can't accidentally fight each other.
 *
 * @param {{agentId?: string, fullScreenUrl: string}} agent
 * @returns {boolean} true if a launcher was successfully invoked.
 */
function openAgentWidget(agent) {
  if (typeof window === "undefined") return false;

  const { agentId, fullScreenUrl } = agent;

  // 1. Try documented global API names that JotForm builds may expose.
  const globals = [
    "AgentInitializer",
    "JotformAgent",
    "JotFormAgent",
    "JFAgent",
  ];
  for (const name of globals) {
    const g = window[name];
    if (g && typeof g.open === "function") {
      try {
        // Some builds accept an agent id; harmless to pass even if ignored.
        agentId ? g.open(agentId) : g.open();
        return true;
      } catch (err) {
        console.warn(`[agents] ${name}.open() failed:`, err);
      }
    }
  }

  // 2. Click the floating launcher button injected by embed.js. When we
  //    know the agentId we prefer selectors that reference it so we
  //    don't open the wrong agent's panel.
  const idScoped = agentId
    ? [
        `iframe[src*="${agentId}"]`,
        `[id*="${agentId}"] button`,
        `button[id*="${agentId}"]`,
      ]
    : [];
  const generic = [
    'button[id^="jotform-agent-launcher"]',
    '[data-testid="jotform-agent-launcher"]',
    'iframe[src*="jotform.com/agent"]',
    '[class*="EmbeddedAgent"] button',
    '[class*="embedded-agent"] button',
    '[id^="EMBEDDED_AGENT"]',
  ];
  for (const sel of [...idScoped, ...generic]) {
    const el = document.querySelector(sel);
    if (el) {
      try {
        el.click?.();
        if (el.tagName === "IFRAME") {
          el.scrollIntoView({ behavior: "smooth", block: "end" });
        }
        return true;
      } catch (err) {
        console.warn(`[agents] click on ${sel} failed:`, err);
      }
    }
  }

  // 3. Last-resort fallback: open the full-screen agent URL in a new tab.
  window.open(fullScreenUrl, "_blank", "noopener,noreferrer");
  return false;
}

async function copyPromptToClipboard(prompt) {
  if (!prompt || typeof navigator === "undefined" || !navigator.clipboard) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(prompt);
    return true;
  } catch (err) {
    console.warn("[agents] clipboard copy failed:", err);
    return false;
  }
}

/**
 * Generic agent waker. Use the named helpers below in components.
 */
async function wakeAgent(agentKey, prompt) {
  const agent = AGENTS[agentKey];
  if (!agent) {
    console.warn(`[agents] unknown agentKey "${agentKey}"`);
    return { opened: false, copied: false };
  }
  const copiedPromise = prompt
    ? copyPromptToClipboard(prompt)
    : Promise.resolve(false);
  const opened = openAgentWidget(agent);
  const copied = await copiedPromise;
  return { opened, copied };
}

/**
 * Wake Maya — practice coach. Optionally pre-loads a suggested prompt
 * onto the clipboard so the member can paste-and-send.
 */
export function wakeMaya(prompt) {
  return wakeAgent("maya", prompt);
}

/**
 * Wake Elara — program guide. Optionally pre-loads a suggested prompt.
 */
export function wakeElara(prompt) {
  return wakeAgent("elara", prompt);
}

/**
 * Wake the Peak State Coach. Since only Maya is mounted as a floating
 * launcher (JotForm allows one per page), Peak State will fall through
 * to opening its full-screen agent URL in a new tab.
 */
export function wakePeakState(prompt) {
  return wakeAgent("peakState", prompt);
}

export const MAYA_FULLSCREEN_URL = AGENTS.maya.fullScreenUrl;
export const ELARA_FULLSCREEN_URL = AGENTS.elara.fullScreenUrl;
export const PEAK_STATE_FULLSCREEN_URL = AGENTS.peakState.fullScreenUrl;
